import {Box, Modal, useMediaQuery} from '@mui/material';
import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {styled} from 'styled-components';
import {A11y, Navigation, Pagination} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';

import {ImageCarouselFullscreenDesktop} from '@website_frontend/components/image_carousel_fullscreen_desktop';
import {ImageCarouselFullscreenMobile} from '@website_frontend/components/image_carousel_fullscreen_mobile';
import {isImageLoaded, markImageAsLoaded, preloadImage} from '@website_frontend/lib/image_cache';

interface ImageCarouselProps {
  images: string[];
  title?: string;
}

export const NO_IMAGE_PLACEHOLDER = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="400" height="300" fill="%23eee"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23999" font-family="Arial" font-size="20">No image</text></svg>`;

export const ImageCarousel: FC<ImageCarouselProps> = props => {
  const {images, title} = props;
  const isMobile = useMediaQuery('(max-width: 512px)');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [shouldLoadImages, setShouldLoadImages] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const loadingStartedRef = useRef(false);

  const paginationConfig = useMemo(() => {
    const numImages = images.length;
    if (numImages <= 6) {
      return {clickable: true, type: 'bullets' as const};
    }
    return {type: 'fraction' as const};
  }, [images.length]);

  // Reset loading state when images change and preload first image (thumbnail)
  useEffect(() => {
    loadingStartedRef.current = false;
    // Initialize loaded images from global cache
    const initiallyLoaded = new Set<string>();
    images.forEach(src => {
      if (isImageLoaded(src)) {
        initiallyLoaded.add(src);
      }
    });
    setLoadedImages(initiallyLoaded);
    if (images.length > 0 && images[0]) {
      // Preload first image (thumbnail) immediately
      preloadImage(images[0]);
    }
  }, [images]);

  // Load images: first 3 sequentially, then rest in parallel
  useEffect(() => {
    if (!shouldLoadImages || images.length === 0 || loadingStartedRef.current) return;

    loadingStartedRef.current = true;
    let isCancelled = false;

    const loadImage = (src: string): void => {
      if (isCancelled) {
        return;
      }

      // Use global cache to preload image (fire-and-forget)
      preloadImage(src);
    };

    const loadImages = () => {
      // Load first 3 images sequentially
      const firstThree = images.slice(0, 3);
      for (const src of firstThree) {
        if (isCancelled) break;
        // Skip if already loaded in global cache
        if (!isImageLoaded(src)) {
          loadImage(src);
        }
      }

      // Load remaining images in parallel
      const remaining = images.slice(3);
      if (remaining.length > 0 && !isCancelled) {
        remaining.forEach(src => {
          if (!isImageLoaded(src)) {
            loadImage(src);
          }
        });
      }
    };

    void loadImages();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLoadImages, images]);

  const handleCloseFullscreen = () => {
    setIsFullscreen(false);
  };

  const noImagePlaceholder =
    'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22400%22 height=%22300%22%3E%3Crect width=%22400%22 height=%22300%22 fill=%22%23eee%22/%3E%3Ctext x=%2250%25%22 y=%2250%25%22 dominant-baseline=%22middle%22 text-anchor=%22middle%22 fill=%22%23999%22 font-family=%22Arial%22 font-size=%2220%22%3ENo image%3C/text%3E%3C/svg%3E';

  const isImageReady = (src: string, index: number): boolean => {
    // First image is always ready (it's preloaded)
    if (index === 0) {
      return true;
    }
    // Check if image has been loaded (check both local state and global cache)
    return loadedImages.has(src) || isImageLoaded(src);
  };

  const handleImageLoad = (src: string) => {
    // Mark image as loaded in both local state and global cache
    markImageAsLoaded(src);
    setLoadedImages(prev => {
      const next = new Set(prev);
      next.add(src);
      return next;
    });
  };

  const swiperRef = useRef<HTMLDivElement>(null);

  // Stop propagation on Swiper navigation buttons and pagination
  useEffect(() => {
    const wrapper = swiperRef.current;
    if (!wrapper) return;

    const handleButtonClick = (e: Event) => {
      e.stopPropagation();
    };

    const navigationButtons = wrapper.querySelectorAll('.swiper-button-next, .swiper-button-prev');
    const pagination = wrapper.querySelector('.swiper-pagination');

    navigationButtons.forEach(button => {
      button.addEventListener('click', handleButtonClick);
    });

    if (pagination) {
      pagination.addEventListener('click', handleButtonClick);
    }

    return () => {
      navigationButtons.forEach(button => {
        button.removeEventListener('click', handleButtonClick);
      });
      if (pagination) {
        pagination.removeEventListener('click', handleButtonClick);
      }
    };
  }, []);

  return (
    <>
      <ImageWrapper onClick={() => setIsFullscreen(true)} style={{cursor: 'pointer'}}>
        <div ref={swiperRef}>
          <Swiper
            modules={[Navigation, Pagination, A11y]}
            spaceBetween={8}
            slidesPerView={1}
            navigation
            pagination={paginationConfig}
            onSlideChange={swiper => {
              setCurrentSlide(swiper.activeIndex);
              setShouldLoadImages(true);
            }}
          >
            {images.length > 0 ? (
              images.map((src, index) => (
                <SwiperSlide key={src}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      paddingTop: '75%', // 4/3 aspect ratio
                      overflow: 'hidden',
                    }}
                  >
                    {!isImageReady(src, index) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backgroundColor: '#f0f0f0',
                          zIndex: 1,
                        }}
                      />
                    )}
                    <img
                      src={src}
                      alt={title || 'Image'}
                      loading={index === 0 || index === currentSlide ? 'eager' : 'lazy'}
                      onLoad={() => handleImageLoad(src)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: isImageReady(src, index) ? 1 : 0,
                        transition: 'opacity 0.15s ease-in-out',
                        zIndex: 2,
                      }}
                    />
                  </Box>
                </SwiperSlide>
              ))
            ) : (
              <SwiperSlide>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    paddingTop: '75%', // 4/3 aspect ratio
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={noImagePlaceholder}
                    alt={title || 'Image'}
                    loading="lazy"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              </SwiperSlide>
            )}
          </Swiper>
        </div>
      </ImageWrapper>
      <Modal
        open={isFullscreen}
        onClose={handleCloseFullscreen}
        sx={{
          zIndex: 10000,
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
            },
          },
        }}
      >
        <ModalContent>
          {isMobile ? (
            <ImageCarouselFullscreenMobile
              images={images}
              title={title}
              onClose={handleCloseFullscreen}
            />
          ) : (
            <ImageCarouselFullscreenDesktop
              images={images}
              title={title}
              currentSlide={currentSlide}
              onClose={handleCloseFullscreen}
              paginationConfig={paginationConfig}
            />
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

ImageCarousel.displayName = 'ImageCarousel';

const ModalContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
`;

const ImageWrapper = styled.div`
  background-color: #e7e7e7;
  width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  border-radius: 6px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;

  @media (max-width: 512px) {
    border-radius: 0;
  }
  
  /* Swiper navigation arrows */
  .swiper-button-next,
  .swiper-button-prev {
    /* Make arrow larger (appears thicker) and more transparent */
    --swiper-navigation-size: 34px;
    color: rgba(0, 0, 0, 0.45);
    width: 40px;
    height: 40px;
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.3);
    backdrop-filter: saturate(120%) blur(2px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
  .swiper-button-next::after,
  .swiper-button-prev::after {
    /* Ensure the icon scales with the button size */
    font-size: 18px;
  }

  /* Pagination background container for both bullets and fraction */
  .swiper-pagination {
    z-index: 2;
    width: fit-content;
    margin: 0 auto;
    background: rgba(255, 255, 255, 0.4);
    border-radius: 10px;
    padding: 0px 8px;
    backdrop-filter: saturate(120%) blur(2px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  }

  /* Swiper bullets */
  .swiper-pagination-bullets.swiper-pagination-horizontal, .swiper-pagination-fraction {
    left: 50%;
    width: auto;
    translate: -50%;
  }
  .swiper-pagination-bullet {
    background: rgba(0, 0, 0, 0.3);
    opacity: 1;
  }
  .swiper-pagination-bullet-active {
    background: rgba(0, 0, 0, 0.6);
  }

  /* Swiper fraction */
  .swiper-pagination-fraction {
    color: rgba(0, 0, 0, 0.6);
    font-weight: 600;
    font-size: 12px;
  }
`;
